import { CrossIcon } from "@solomon-labs/design/components/icons";
import {
    ButtonIcon,
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogTitle,
    FormActions
} from "@solomon-labs/design/components/ui";
import type React from "react";

import { textVariants } from "./text";

export interface ConfirmModalProps {
    /** Whether the modal is open. */
    open: boolean;

    /**
     * Fires whenever the open state changes — called with `false` for
     * any user-initiated dismissal: escape key, backdrop click, or
     * the close-X button. Map this back to your local open state.
     */
    onOpenChange: (open: boolean) => void;

    /** Modal title — rendered as the dialog's accessible name. */
    title: string;

    /**
     * Title typography. `body` (default) keeps the small title the main app
     * adopts; `h2` matches the DS `DialogTitle` / `AlertDialogTitle` default,
     * which the consoles' other dialogs use, so a console confirm reads as
     * one of them.
     */
    titleVariant?: "body" | "h2";

    /**
     * Optional supporting copy below the title. A node, so a line can hold
     * an inline element (e.g. a `ResponsiveAddress`).
     */
    description?: React.ReactNode;

    /** Label for the primary (confirm) button. */
    confirmLabel: string;

    /** Fires when the primary button is clicked. */
    onConfirm: () => void;

    /**
     * Renders the primary button in a destructive tone (ember
     * surface, paper foreground) instead of the default raised
     * charcoal. Use for delete, remove, suspend, reject, revoke
     * actions.
     *
     * TODO(button): when `Button` grows a proper destructive
     * variant, replace the className override below with
     * `variant="destructive"` (or whatever lands).
     */
    destructive?: boolean;

    /**
     * Disable the primary button while keeping the modal open.
     * Common during async submission or when the required input
     * (e.g. a reason textarea) isn't valid yet.
     */
    confirmDisabled?: boolean;

    /**
     * Optional content rendered between the description and the
     * footer — used for inputs that the user must fill in before
     * confirming (reason textareas, severity pickers, etc.). For
     * pure confirms, leave this undefined.
     */
    children?: React.ReactNode;
}

/**
 * `ConfirmModal` — the canonical "Are you sure?" interstitial.
 *
 * Composes `Dialog` from `ui/dialog.tsx`. All modal-shell concerns
 * (portal, backdrop blur, paper surface, accent top border,
 * animations, focus trap, escape key, `role="dialog"`,
 * `aria-modal`, backdrop-click dismiss) are handled by the
 * underlying primitive.
 *
 * Footer is a left-aligned `FormActions` pair: a raised+lifted primary
 * (the action) beside a transparent+underline "Cancel" that just closes
 * the modal (identical to the close-X / backdrop / escape back-out paths).
 *
 * For destructive actions, set `destructive` to tone the primary
 * button. For domain-specific confirms (e.g. `SuspendModal`,
 * `RejectModal`), wrap this in a thin named component that
 * pre-fills `title` / `description` / `confirmLabel` and passes a
 * reason input through `children`. See ARCHITECTURE.md principle
 * #2 for the rationale.
 */
export function ConfirmModal({
    open,
    onOpenChange,
    title,
    titleVariant = "body",
    description,
    confirmLabel,
    onConfirm,
    confirmDisabled = false,
    children
}: ConfirmModalProps): React.ReactElement {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-[440px] p-6">
                {/* Header row — title and dismiss-X share a single row.
                 *
                 * Title/description override Dialog's baked-in
                 * h2/body variants with body/small respectively. The
                 * smaller scale keeps ConfirmModal v3-ready: main app
                 * deliberately avoids the dense console heading
                 * variants (h2/h3) at non-hero positions, so using
                 * body for the title means main can adopt
                 * ConfirmModal without typography conflicts. Consoles
                 * pass `titleVariant="h2"` to match their other dialogs.
                 */}
                <div className="flex items-center justify-between gap-3">
                    <DialogTitle
                        className={textVariants({ variant: titleVariant })}
                    >
                        {title}
                    </DialogTitle>
                    <DialogClose
                        render={
                            <ButtonIcon
                                variant="transparent"
                                size="xl"
                                className="shrink-0"
                                aria-label="Close"
                                icon={<CrossIcon />}
                            />
                        }
                    />
                </div>

                {description && (
                    <DialogDescription
                        className={textVariants({ variant: "small" })}
                    >
                        {description}
                    </DialogDescription>
                )}

                {children}

                {/*
                 * Footer — standard raised + lifted Button. Extra top
                 * padding so the action sits visually separate from
                 * the body copy.
                 *
                 * The `destructive` prop is currently a structural
                 * placeholder — visually no-op until Button grows a
                 * proper destructive variant. Keeping it in the API
                 * means consumers don't need to change when that
                 * variant lands. See `confirm-modal.intent.md` for
                 * the full TODO note.
                 */}
                <FormActions
                    className="pt-4"
                    primaryLabel={confirmLabel}
                    onPrimary={onConfirm}
                    primaryDisabled={confirmDisabled}
                    cancelLabel="Cancel"
                    onCancel={() => onOpenChange(false)}
                />
            </DialogContent>
        </Dialog>
    );
}
